import { paths } from "config/paths";
import { useRouter } from "next/router";
import { FC, ReactNode } from "react";
import {
	hasEqualOrHigherAccessThanSpecifiedRole,
	isDatasetCreator,
	isMapCreator,
} from "~auth/helpers";
import { useGetCurrentAccount } from "~auth/queries/useGetCurrentAccount";
import { useGetCurrentUser } from "~auth/queries/useGetCurrentUser";
import { ACCOUNT_USER_ROLE } from "~auth/types/__generated/gql/graphql";
import { useGetCurrentDataset } from "~dataset-view/queries/useGetDatasetById";
import { useGetCurrentMap } from "~map-view/queries/useGetMapById";
import { MangoLogoLoadingBackdrop } from "~ui-components/index";

export interface AuthenticatedRouteGuardProps {
	children: ReactNode;
	onlyMapCreator?: true;
	onlyDatasetCreator?: true;
	minimumAccessLevel?: `${ACCOUNT_USER_ROLE}`;
}

export const AuthenticatedRouteGuard: FC<AuthenticatedRouteGuardProps> = ({
	children,
	onlyDatasetCreator,
	onlyMapCreator,
	minimumAccessLevel,
}) => {
	const router = useRouter();

	const { isLoading: isUserLoading, data: user } = useGetCurrentUser();
	const { isLoading: isAccountLoading, data: account } = useGetCurrentAccount();
	const { isLoading: isMapLoading, data: map } = useGetCurrentMap();
	const { isLoading: isDatasetLoading, data: dataset } = useGetCurrentDataset();

	let isDatasetCreatorRulePassed = false;
	if (onlyDatasetCreator) {
		if (isDatasetLoading || isUserLoading) return <MangoLogoLoadingBackdrop />;

		isDatasetCreatorRulePassed = isDatasetCreator(dataset, user);
	}

	let isMapCreatorRulePassed = false;
	if (onlyMapCreator) {
		if (isMapLoading || isUserLoading) return <MangoLogoLoadingBackdrop />;
		isMapCreatorRulePassed = isMapCreator(map, user);
	}

	let isMinimumAccessLevelPassed = false;
	if (minimumAccessLevel !== undefined) {
		if (isAccountLoading) return <MangoLogoLoadingBackdrop />;

		isMinimumAccessLevelPassed = hasEqualOrHigherAccessThanSpecifiedRole(
			account,
			minimumAccessLevel,
		);
	}

	const isAllRulesFailed = [
		isDatasetCreatorRulePassed,
		isMapCreatorRulePassed,
		isMinimumAccessLevelPassed,
	].every((bool) => bool === false);

	if (isAllRulesFailed) {
		void router.replace(paths.unauthorized);
		return <MangoLogoLoadingBackdrop />;
	}

	return children;
};
